import { defineNuxtPlugin, useRouter } from '#app';
import { nextTick } from 'vue';
import {
  blogPaginationKey,
  blogPaginationScrollPosition
} from '~/utils/blog-pagination';

export default defineNuxtPlugin({
  name: 'blog-pagination-scroll',
  setup(nuxtApp) {
    const router = useRouter();
    // Vue Router exposes its original options object. Decorate just this case;
    // leave Nuxt in charge of all other pages, anchors and transition promises.
    // Nuxt restores its initial scrolling options in app:created. Install after
    // hydration, so our decorator is not overwritten by that initial handover.
    nuxtApp.hook('app:mounted', () => {
      const original = router.options.scrollBehavior;
      router.options.scrollBehavior = function (to, from, savedPosition) {
        const position = blogPaginationScrollPosition(to, from, savedPosition);
        if (position !== undefined) {
          if (!savedPosition) return position;
          // Saved positions must be applied to the actual finished list, not to
          // a shorter loading skeleton where the browser would clamp scrollY.
          const grid = document.querySelector('.grid-shell');
          if (!grid) return position;
          const key = blogPaginationKey(to.query);
          return new Promise((resolve) => {
            let unroute = () => {};
            let settled = false;
            const cleanup = () => {
              observer.disconnect();
              unroute();
            };
            const finish = async () => {
              if (
                settled ||
                grid.getAttribute('aria-busy') !== 'false' ||
                grid.getAttribute('data-pagination-key') !== key
              )
                return;
              settled = true;
              cleanup();
              const animations = grid.getAnimations?.({ subtree: true }) || [];
              await Promise.allSettled(
                animations
                  .filter(
                    (a) => a.effect?.getComputedTiming().iterations !== Infinity
                  )
                  .map((a) => a.finished)
              );
              await nextTick();
              resolve(
                router.currentRoute.value.fullPath === to.fullPath
                  ? position
                  : false
              );
            };
            const observer = new MutationObserver(finish);
            observer.observe(grid, {
              attributes: true,
              attributeFilter: ['aria-busy', 'data-pagination-key']
            });
            unroute = router.afterEach((target) => {
              if (target.fullPath === to.fullPath || settled) return;
              settled = true;
              cleanup();
              resolve(false);
            });
            void finish();
          });
        }
        return original?.call(this, to, from, savedPosition);
      };
    });
  }
});
